import { FC } from "react"
import { Stack, Theme, Typography, useMediaQuery } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { Products } from "../../../../../../merchant/admin/priceList/priceListTypes"
import { DiscountType, DisplayLabelType } from "../../../../../../merchant/admin/products/productsTypes"
import { MonetaryAmount } from "../../../../../../currency/currencyTypes"
import { toDinero } from "../../../../../../currency/currencyLib"
import { Dinero } from "dinero.js"
import { useMoney } from "../../../../../../currency/useMoney"
import { tailwindPalette } from "../../../../../../theme/tailwindPalette"

interface ProductOfferAmountProps {
    product: Products
}

const useStyles = makeStyles()((theme) => ({
    amountWrapper: {
        fontSize: theme.typography.h5.fontSize,
        textAlign: "center",
        whiteSpace: "nowrap",
    },
    originalPrice: {
        textDecoration: "line-through",
        color: tailwindPalette.slate[500],
        fontSize: ".65rem",
    },
    currentPrice: {
        fontSize: ".875rem",
        color: tailwindPalette.red[500],
    },
    normalPrice: {
        color: tailwindPalette.slate[500],
        fontSize: ".875rem",
    },
}))

export const ProductOfferAmount: FC<ProductOfferAmountProps> = ({ product }) => {
    const { classes } = useStyles()

    const isLandscape = useMediaQuery((theme: Theme) => theme.breakpoints.up("lg"))

    const isOldNewPrice = product.offerDetails?.discount?.displayLabelType === DisplayLabelType.OLD_NEW_PRICE

    const originalPrice =
        product.offerDetails?.discount?.type === DiscountType.DISCOUNT_PRICE
            ? product.offerDetails.discount.price
            : null

    const currentPrice = product.price

    return (
        <div className={classes.amountWrapper}>
            {isOldNewPrice && originalPrice ? (
                <Stack direction="row" alignItems="center" spacing="0.625rem">
                    <Typography variant="body2" className={classes.currentPrice}>
                        <Amount showCurrency={isLandscape} amount={currentPrice} />
                    </Typography>
                    <Typography variant="body2" className={classes.originalPrice}>
                        <Amount showCurrency={isLandscape} amount={originalPrice} />
                    </Typography>
                </Stack>
            ) : (
                <Typography variant="body2" className={classes.normalPrice}>
                    <Amount showCurrency={isLandscape} amount={currentPrice} />
                </Typography>
            )}
        </div>
    )
}

interface AmountProps {
    amount: MonetaryAmount
    showCurrency?: boolean
}

export function Amount({ amount, showCurrency = true }: AmountProps) {
    const dinero = toDinero(amount)

    if (showCurrency) {
        return <DineroAmount amount={dinero} />
    }

    return <DineroAmountWithoutCurrency amount={dinero} />
}

type DineroAmountProps = Readonly<{
    amount: Dinero<number>
}>

export function DineroAmountWithoutCurrency({ amount }: DineroAmountProps) {
    const moneyFactory = useMoney({})

    return <>{moneyFactory.formatAsNumber(amount)}</>
}

export function DineroAmount({ amount }: DineroAmountProps) {
    const moneyFactory = useMoney({})

    return <>{moneyFactory.format(amount)}</>
}
